<template>
  <v-app :class="appClasses">
    <Toasts />
    <router-view :key="$route.path" />
    <NewContentDialog />
  </v-app>
</template>

<script lang="ts">
    import Vue from 'vue';
    import NewContentDialog from '@/components/NewContentDialog.vue';
    import UserPlanMixin from "@/mixins/UserPlanMixin";
    import {LocalStorage} from './plugins/LocalStorage';
    import "./styles/common.css";
    import "./styles/tokens.css";
    import "./styles/design-surfaces.css";
    import { Toast } from '@/model/Toast'
    import Toasts from "@/components/Toasts.vue";
    import { Firestore } from '@/plugins/Firestore'
    import { UiSettings } from '@/plugins/UiSettings';
    import {runWhenPlanLoaded} from '@/service/articleLimitGate';

    export default Vue.extend({
        name: 'App',
        components: {NewContentDialog, Toasts},
        mixins: [UserPlanMixin],
        data: () => ({
            authUnWatch: null as null | (() => void),
            planUnWatch: null as null | (() => void),
            actmUnWatch: null as null | (() => void),
            uiSettings: UiSettings,
        }),
        computed: {
            appClasses(): string[] {
                return [`skin--${this.uiSettings.skin}`];
            },
        },
        created: function () {
            this.handleQueryParams();
            this.observeFreeAnalysesCount();
        },
        mounted() {
            const pref = UiSettings.themePreference;
            if (pref === 'dark') {
                this.$vuetify.theme.dark = true;
                LocalStorage.setTheme(true);
            } else if (pref === 'light') {
                this.$vuetify.theme.dark = false;
                LocalStorage.setTheme(false);
            } else if (pref === 'system' && typeof window !== 'undefined' && window.matchMedia) {
                const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                this.$vuetify.theme.dark = dark;
            } else {
                this.$vuetify.theme.dark = LocalStorage.getTheme();
            }
            this.$vuetify.theme.themes.light.primary = this.uiSettings.primaryColor;
            this.$vuetify.theme.themes.dark.primary  = this.uiSettings.primaryColor;
        },
        watch: {
            '$route'(to: {name?: string | null; query: Record<string, unknown>}) {
                const keepShare = to.name === 'SharedArticle'
                    || (to.name === 'SeoScoreDetail' && !!(this.$store.getters.sharePreview || to.query.share));
                if (!keepShare) {
                    this.$store.commit('clearSharePreview');
                }
            }
        },
        beforeDestroy() {
            Firestore.stopObservingFreeAnalysesConfig();
        },
        methods: {
            handleQueryParams: function () {
                const {keyword, langloc, via} = this.$route.query;
                if (via) {
                    // Rewardful handles its business in the included script in index.html
                }
                if (keyword || langloc) {
                    this.redirectToArticleCreation(keyword, langloc);
                }
            },
            redirectToArticleCreation: function (keyword: string | (string | null)[], langloc: string | (string | null)[]) {
                if (this.$store.getters.authenticated) {
                    if (!this.planLoaded) {
                        runWhenPlanLoaded(this, () => this.redirectToArticleCreation(keyword, langloc));
                        return;
                    }
                    if (this.hasAnyCredit) {
                        if (!this.loadingArticlesCreatedThisPeriod) {
                            // Check if user can generate articles (createArticle is generate mode by default)
                            if (this.hasCreditForGenerate) {
                                this.$router.replace({
                                    path: '/createArticle',
                                    query: this.$route.query
                                });
                            } else {
                                // User has no credit - distinguish between no plan vs reached limit
                                if (!this.hasPlan) {
                                    // User has no active plan at all
                                    this.$store.dispatch('toasts/add', {
                                        type: 'error',
                                        title: 'subscriptions.paywall.generateRequiresPlan.title',
                                        content: 'subscriptions.paywall.generateRequiresPlan.content',
                                        action: {
                                            text: 'subscriptions.paywall.generateRequiresPlan.action',
                                            callback: () => {}
                                        }
                                    } as Toast);
                                } else {
                                    // User has a plan but reached their limit
                                    this.$store.dispatch('toasts/add', {
                                        type: 'error',
                                        title: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.title',
                                        content: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.content',
                                        action: {
                                            text: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.action',
                                            callback: () => {}
                                        }
                                    } as Toast);
                                }
                                this.$router.replace({
                                    path: '/account',
                                    query: this.$route.query
                                });
                            }
                        } else {
                            if (this.actmUnWatch === null) {
                                this.actmUnWatch = this.$watch('loadingArticlesCreatedThisPeriod', value => {
                                    if (!value) {
                                        this.actmUnWatch && this.actmUnWatch();
                                        this.redirectToArticleCreation(keyword, langloc);
                                    }
                                });
                            }
                        }
                    } else {
                        if (this.planUnWatch === null) {
                            this.planUnWatch = this.$watch('hasAnyCredit', value => {
                                if (value) {
                                    this.planUnWatch && this.planUnWatch();
                                    this.redirectToArticleCreation(keyword, langloc);
                                }
                            });
                        }
                    }
                } else {
                    if (this.authUnWatch === null) {
                        this.authUnWatch = this.$watch('$store.getters.authenticated', value => {
                            if (value) {
                                this.authUnWatch && this.authUnWatch();
                                this.redirectToArticleCreation(keyword, langloc);
                            }
                        });
                    }
                }
            },
            observeFreeAnalysesCount: function () {
                Firestore.observeFreeAnalysesConfig((count) => {
                    this.$store.commit('setMaxFreeAnalyses', count);
                });
            }
        }
    });
</script>

<style lang="sass">
// ─── Base ────────────────────────────────────────────────────────────────────
.v-application,
.v-application--wrap
    background: var(--v-background-base)
    font-family: var(--sw-font-ui)

// CKEditor article body: keep pre–Public Sans typeface (Roboto stack), not UI chrome.
.ck.ck-editor__editable_inline,
.ck.ck-content
    font-family: 'Roboto', sans-serif

// ─── App bar gradient ────────────────────────────────────────────────────────
.primaryGradientBackground
    background: linear-gradient(120deg, #0c98f5 0%, #0566c3 100%) !important

    ::selection
        background: var(--v-accent-base)

    .v-application & .error--text
        color: #fffb00 !important
        caret-color: #fffb00 !important

    .v-application & .primary--text
        color: white !important
        caret-color: white !important

thead.primaryGradientBackground
    background-attachment: fixed

.primaryBorder
    border-color: var(--sw-primary) !important
    border-width: 2px
    border-style: solid

.dark-background
    .v-application & .primary--text
        color: white !important
        caret-color: white !important

// ─── Cards – modern rounded style ─────────────────────────────────────────────
.v-card
    border-radius: var(--sw-radius-md) !important
    transition: box-shadow 0.2s ease, transform 0.15s ease !important

.theme--light .v-card
    box-shadow: var(--sw-shadow-md) !important

.theme--light .v-card.v-sheet--outlined
    box-shadow: none !important
    border: 1px solid rgba(58, 53, 65, 0.16) !important

.theme--dark .v-card
    box-shadow: var(--sw-shadow-md) !important
    background-color: var(--v-surface-base) !important
    border: 1px solid var(--sw-neutral-border)

// Bordered skin override
.skin--bordered
    .theme--light .v-card
        border: 1px solid rgba(58, 53, 65, 0.16) !important
        box-shadow: none !important

    .theme--dark .v-card
        border: 1px solid rgba(255, 255, 255, 0.12) !important
        box-shadow: none !important

// Elevate on hover for interactive cards
.v-card.v-card--link:hover
    box-shadow: var(--sw-shadow-lg) !important
    transform: translateY(-2px)

// Card title styling
.v-card__title
    font-weight: 600 !important
    letter-spacing: 0.005em !important

// ─── App bar shadow ──────────────────────────────────────────────────────────
.v-app-bar.primaryGradientBackground
    box-shadow: 0 4px 20px rgba(5, 102, 195, 0.30) !important

// ─── Navigation drawer ────────────────────────────────────────────────────────
.v-navigation-drawer.theme--dark
    background-color: var(--v-surface-base) !important

.v-navigation-drawer.theme--light
    background-color: #ffffff !important
    box-shadow: 4px 0 18px rgba(58, 53, 65, 0.08) !important

// ─── Slide group ──────────────────────────────────────────────────────────────
.v-slide-group__prev
    border-radius: 50px
    margin: 0 10px
    height: 50px
    align-self: center

.v-slide-group__next
    border-radius: 50px
    margin: 0 10px
    height: 50px
    align-self: center

// ─── List / density ──────────────────────────────────────────────────────────
.v-list-item--superdense
    min-height: 28px !important
    line-height: 1.25rem

    .text-caption
        line-height: .9rem

// ─── Typography helpers ────────────────────────────────────────────────────────
.text-decoration-dotted
    text-decoration: underline dotted
    text-underline-offset: .15em

.text-decoration-none
    text-decoration: none

.v-card__title
    word-break: normal

.text-wrap-balance
    text-wrap: balance

.font-weight-semibold
    font-weight: 600 !important

// ─── Buttons ──────────────────────────────────────────────────────────────────
.v-application .v-btn
    border-radius: var(--sw-radius-button) !important
    letter-spacing: 0.02em !important

.v-application .v-chip
    border-radius: var(--sw-radius-chip) !important

// Filled accent / success / warning / error share the SEO traffic-light trio.
.v-application .v-btn.accent:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--plain),
.v-application .v-btn.success:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--plain)
    background-color: var(--sw-success-fill, #4e8a1f) !important
    border-color: var(--sw-success-fill, #4e8a1f) !important
    color: #ffffff !important
    caret-color: #ffffff !important

    .v-icon,
    .v-btn__loader .v-progress-circular
        color: #ffffff !important

.v-application .v-btn.warning:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--plain)
    background-color: var(--v-warning-base) !important
    border-color: var(--v-warning-base) !important
    color: #fff !important
    caret-color: #fff !important

    .v-icon,
    .v-btn__loader .v-progress-circular
        color: #fff !important

.v-application .v-btn.error:not(.v-btn--outlined):not(.v-btn--text):not(.v-btn--plain)
    color: #fff !important
    caret-color: #fff !important

.v-application .v-chip.accent:not(.v-chip--outlined),
.v-application .v-chip.success:not(.v-chip--outlined),
.v-application .v-chip.warning:not(.v-chip--outlined),
.v-application .v-chip.error:not(.v-chip--outlined)
    color: #fff !important

    .v-chip__content,
    .v-icon
        color: #fff !important

.v-application .v-alert.success:not(.v-alert--outlined):not(.v-alert--text),
.v-application .v-alert.warning:not(.v-alert--outlined):not(.v-alert--text),
.v-application .v-alert.error:not(.v-alert--outlined):not(.v-alert--text),
.v-application .v-alert.accent:not(.v-alert--outlined):not(.v-alert--text)
    color: #fff !important

.multi-line-button
    display: inline-block
    min-height: 3em
    height: auto !important
    white-space: normal
    padding: 5px 15px !important

.heading-generate-btn
    width: 100%
    max-width: 100%

// ─── Tooltips ─────────────────────────────────────────────────────────────────
.v-tooltip__content
    max-width: 400px
    border-radius: var(--sw-radius-sm) !important

// ─── Article layout ───────────────────────────────────────────────────────────
.article-content
    width: 70vw
    max-width: 70vw
    box-sizing: border-box

// Soft surface for editor blocks (heading, paragraph, meta, FAQ, TOC, …)
.workspace-block
    background: var(--v-blockSurface-base)
    border: 1px solid rgba(58, 53, 65, 0.055)
    border-radius: var(--sw-radius-md)
    padding: 10px 14px 8px
    width: 100%
    max-width: 100%
    min-width: 0
    box-sizing: border-box

.theme--dark .workspace-block
    border-color: rgba(255, 255, 255, 0.07)

.theme--light .v-card.workspace-block
    background: var(--v-blockSurface-base) !important
    box-shadow: none !important
    border: 1px solid rgba(58, 53, 65, 0.055) !important
    padding: 0

.theme--dark .v-card.workspace-block
    background: var(--v-blockSurface-base) !important
    box-shadow: none !important
    border: 1px solid rgba(255, 255, 255, 0.07) !important
    padding: 0

.article-inspiration
    margin-right: 24px
    max-width: calc(50% - 24px)
    position: sticky
    top: 66px
    max-height: calc(100vh - 66px)
    overflow: auto

// ─── Caption / link text ─────────────────────────────────────────────────────
.text-caption-primary
    font-size: 0.75rem !important
    font-weight: 400
    line-height: 1.25rem
    letter-spacing: 0.0333333333em !important
    font-family: var(--sw-font-ui) !important
    color: var(--v-anchor-base)

.chip-text-primary
    color: var(--sw-primary) !important
    border: none !important
    padding-left: 0 !important

// ─── Table ────────────────────────────────────────────────────────────────────
.v-data-table__wrapper
    border-radius: 10px !important

.v-data-table
    border-radius: 12px !important

// ─── Pie/stat card ─────────────────────────────────────────────────────────── 
.pie-card
    box-shadow: var(--sw-shadow-sm) !important
    border-radius: var(--sw-radius-md) !important

// ─── CKEditor ────────────────────────────────────────────────────────────────
.ck-powered-by
    display: none !important

// ─── Scrollbar ───────────────────────────────────────────────────────────────
html
    & > ::-webkit-scrollbar-track
        background: var(--v-background-base)

::-webkit-scrollbar
    width: 6px

::-webkit-scrollbar-track
    background: transparent

::-webkit-scrollbar-thumb
    background: rgba(0,0,0,0.2)
    border-radius: 6px

    .theme--dark &
        background: rgba(255,255,255,0.2)

::-webkit-scrollbar-thumb:hover
    background: rgba(0,0,0,0.35)

    .theme--dark &
        background: rgba(255,255,255,0.35)

// ─── Transform helpers ───────────────────────────────────────────────────────
.rotate-180
    transform: rotate(180deg)

// ─── Text markers ────────────────────────────────────────────────────────────
.marker-yellow
    .theme--light &
        color: var(--v-markerYellow-darken2)
    .theme--dark &
        color: var(--v-markerYellow-base)

.marker-green
    .theme--light &
        color: var(--v-markerGreen-darken2)
    .theme--dark &
        color: var(--v-markerGreen-base)

.marker-blue
    .theme--light &
        color: var(--v-markerBlue-darken2)
    .theme--dark &
        color: var(--v-markerBlue-base)

.marker-pink
    .theme--light &
        color: var(--v-markerPink-darken2)
    .theme--dark &
        color: var(--v-markerPink-base)

.marker-green, .marker-blue, .marker-pink, .marker-yellow
    background-color: unset !important
    font-weight: bold

    .theme--dark &
        text-shadow: 0 0 20px white

// ─── Card list transition ────────────────────────────────────────────────────
.card-list-item
    transition: all .3s

.card-list-enter, .card-list-leave-to
    opacity: 0
    transform: translateY(-60px)

.card-list-leave-active
    position: absolute
    width: 100%

// ─── Theme customizer drawer shadow ──────────────────────────────────────────
.theme-customizer.v-navigation-drawer
    box-shadow: -4px 0 24px rgba(0,0,0,0.12) !important

    &.theme--dark
        background-color: #2f3349 !important
</style>
